import { supabase } from "../../supabase/supabase";
import { CallLog, CallLogFilters, CallLogsResponse } from "../models/CallLog";

export const callLogService = {
  async getCallLogs(
    filters: CallLogFilters = {},
  ): Promise<CallLogsResponse> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
      }

      // Build the URL with query parameters
      const url = new URL(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_call_logs`,
      );

      // No need to add organization ID as it will be determined by auth.oid() in the backend

      // Add filters
      if (filters.organizationId) {
        url.searchParams.append("organizationId", filters.organizationId);
      }

      if (filters.campaignId) {
        url.searchParams.append("campaignId", filters.campaignId);
      }

      if (filters.assistantId) {
        url.searchParams.append("assistantId", filters.assistantId);
      }

      if (filters.status) {
        url.searchParams.append("status", filters.status);
      }

      if (filters.endedReason) {
        url.searchParams.append("endedReason", filters.endedReason);
      }

      if (filters.direction) {
        url.searchParams.append("direction", filters.direction);
      }

      if (filters.startDate) {
        url.searchParams.append("startDate", filters.startDate);
      }

      if (filters.endDate) {
        url.searchParams.append("endDate", filters.endDate);
      }

      if (filters.search) {
        url.searchParams.append("search", filters.search);
      }

      // Add pagination parameters
      url.searchParams.append("limit", String(filters.limit || 20));
      
      // Choose pagination method
      const method = filters.method || 'cursor'; // Default to cursor for better performance
      url.searchParams.append("method", method);
      
      if (method === 'cursor' && filters.cursor) {
        url.searchParams.append("cursor", filters.cursor);
      } else if (method === 'offset') {
        url.searchParams.append("offset", String(filters.offset || 0));
      }
      
      // Add optional transcript/summary flags
      if (filters.includeTranscript) {
        url.searchParams.append("includeTranscript", "true");
      }
      
      if (filters.includeSummary) {
        url.searchParams.append("includeSummary", "true");
      }

      // Call the edge function
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch call logs");
      }

      const result = await response.json();
      return result as CallLogsResponse;
    } catch (error) {
      console.error("Error fetching call logs:", error);
      throw error;
    }
  },

  async getCallLogById(id: string): Promise<CallLog | null> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;

      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.");
      }

      // Use the new detail endpoint for full call log data
      const url = new URL(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get_call_log_details`,
      );
      url.searchParams.append("id", id);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch call log details");
      }

      const result = await response.json();
      return result.data as CallLog;
    } catch (error) {
      console.error(`Error fetching call log with id ${id}:`, error);
      throw error;
    }
  },
};
